import { sanitize } from 'isomorphic-dompurify'

/**
 * Sanitize HTML content from CMS or user input to prevent XSS attacks.
 * Uses isomorphic-dompurify which works in both server and client contexts.
 *
 * @param dirty - The untrusted HTML string to sanitize
 * @returns The sanitized HTML string safe for rendering
 */
function sanitizeHtml(dirty: string): string {
  if (!dirty || typeof dirty !== 'string') return ''

  return sanitize(dirty, {
    // Allow standard HTML tags plus SVG by default
    USE_PROFILES: { html: true, svg: true },
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  }) as unknown as string
}

/**
 * Sanitize an HTML string that may contain embedded iframe/chart content from CMS.
 * More permissive: allows iframes (for embeds) in addition to standard HTML + SVG.
 *
 * @param dirty - The untrusted HTML string to sanitize
 * @returns The sanitized HTML string safe for rendering
 */
function sanitizeCmsHtml(dirty: string): string {
  if (!dirty || typeof dirty !== 'string') return ''

  return sanitize(dirty, {
    USE_PROFILES: { html: true, svg: true },
    ADD_TAGS: ['iframe'],
    ADD_ATTR: [
      'allow',
      'allowfullscreen',
      'frameborder',
      'scrolling',
      'src',
      'width',
      'height',
      'title',
      'loading',
    ],
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  }) as unknown as string
}

/**
 * Sanitize SVG content from CMS. Only allows SVG-related tags and attributes.
 *
 * @param dirty - The untrusted SVG string to sanitize
 * @returns The sanitized SVG string safe for rendering
 */
function sanitizeSvg(dirty: string): string {
  if (!dirty || typeof dirty !== 'string') return ''

  return sanitize(dirty, {
    USE_PROFILES: { svg: true, svgFilters: true },
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  }) as unknown as string
}

export { sanitizeHtml, sanitizeCmsHtml, sanitizeSvg }
